import { defineStore } from "pinia";
import ChatService from "@/services/ChatService";
import { reactive, toRefs, computed } from "vue";
import { Chat, Message, ChatStoreState } from "@/features/chat/chat.types";
import { useAuth } from "@/services/AuthService";
import { SchultenMedia } from "@/services/DataService";
import { formatMessageTime } from "@/utils/messageDates";

// Outcome of a send. A failure never touches `state.error`: that field replaces the
// whole conversation and chat list. The caller shows `error` in a toast; "" means
// it has no message worth showing.
type SendResult = { message: Message; error?: undefined } | { message?: undefined; error: string }

export const useChatStore = defineStore('chat', () => {
    // Reactive state for chats, messages, and real-time features
    const state = reactive<ChatStoreState>({
        chats: [],
        currentChat: null,
        messages: [],
        isLoading: false,
        hasFetched: false,
        error: null,
        isTyping: false,
        typingUsers: new Set(),
        seenMessageIds: new Set<number>(),
    });

    // De aanvraag die nu loopt, met het gesprek en de pagina waar hij bij hoort,
    // zodat een tweede aanroep weet of dit zijn antwoord is of dat van een ander.
    let pageRequest: { key: string; promise: Promise<void> } | null = null

    // Pagination state for current conversation
    const messagePagination = reactive({
        currentPage: 1,
        lastPage: 1,
        isLoadingPage: false,
        hasMore: true,
    })

    // Debounce controller for typing API
    let typingAbortController: AbortController | null = null
    let typingDebounceTimer: number | undefined

    // Get current user ID from auth service
    const { user } = useAuth();
    const currentUserId = computed(() => (user.value as any)?.id);

    // Computed properties
    const unreadChatsCount = computed(() =>
        state.chats.reduce((count, chat) => count + chat.unread_count, 0)
    );

    // Sorting moved to ChatPage to avoid pre-fetch ordering

    // Fetch chats and update the store's state
    async function fetchChats() {
        // Don't fetch chats if user is not authenticated
        if (!SchultenMedia.Authenticated) {
            return;
        }

        state.isLoading = true;
        state.error = null;

        try {
            const response = await ChatService.getChats();

            if (response.success && response.data) {
                state.chats = Array.isArray(response.data) ? response.data : [];
            } else {
                state.error = response.message;
                state.chats = [];
            }
        } catch (err) {
            state.error = err;
            state.chats = [];
        } finally {
            state.isLoading = false;
            state.hasFetched = true;
        }
    }

    // Ensure a chat exists in the list, otherwise refetch
    function upsertChatFromChatListEvent(payload: any) {
        // Expected payload: { conversation_id, message: {...}, unread_count? }
        const conversationId = payload?.conversation_id || payload?.conversationId || payload?.id;
        const message: Message | undefined = payload?.message;
        if (!conversationId) return;

        const existing = state.chats.find(c => c.id === Number(conversationId));
        if (existing) {
            if (message) {
                existing.last_message = message;
                existing.updated_at = message.created_at;
            }
            // If current chat is not open, bump unread
            if (!state.currentChat || state.currentChat.id !== Number(conversationId)) {
                existing.unread_count = (existing.unread_count || 0) + 1;
            }
        } else {
            // We don't know full chat details; safest to refresh list
            fetchChats();
        }
    }

    // Set current chat and load messages
    function setCurrentChat(chat: Chat) {
        state.currentChat = chat;
        // Reset messages & pagination
        state.messages = [];
        state.seenMessageIds?.clear();
        messagePagination.currentPage = 1;
        messagePagination.lastPage = 1;
        messagePagination.isLoadingPage = false;
        messagePagination.hasMore = true;
        pageRequest = null;
        // Fetch first page
        void fetchConversationMessages(chat.uuid, 1)
        // Mark chat as read
        if (chat.unread_count > 0) {
            markChatAsRead(chat.id);
        }
    }

    // Mark chat as read
    async function markChatAsRead(conversationId: number) {
        try {
            const response = await ChatService.chatRead(conversationId);
            if (response.success) {
                // Update unread count in the chat
                const chat = state.chats.find(c => c.id === conversationId);
                if (chat) {
                    chat.unread_count = 0;
                }
            }
        } catch (error) {
            console.error('Failed to mark chat as read:', error);
        }
    }

    // Fetch paginated messages
    async function fetchConversationMessages(conversationUuid: string, page: number) {
        const key = `${conversationUuid}#${page}`

        while (pageRequest) {
            // Precies deze pagina loopt al: haak aan in plaats van meteen niets te
            // doen. `setCurrentChat` start pagina 1 zonder te wachten, en een
            // `await` hierop moet de berichten opleveren en niet een lege lijst.
            if (pageRequest.key === key) return pageRequest.promise
            // Een andere pagina wacht op zijn beurt. Meteen het antwoord van die
            // lopende aanvraag teruggeven zou betekenen dat deze pagina nooit
            // wordt opgehaald - precies wat de gebruiker bij terugscrollen vraagt.
            await pageRequest.promise
        }

        const request = { key, promise: loadPage(conversationUuid, page) }
        pageRequest = request
        try {
            await request.promise
        } finally {
            // Alleen opruimen als deze aanvraag nog de lopende is: wissel je van
            // gesprek, dan staat hier al de aanvraag van het nieuwe gesprek.
            if (pageRequest === request) pageRequest = null
        }
    }

    async function loadPage(conversationUuid: string, page: number) {
        messagePagination.isLoadingPage = true;
        try {
            const res = await ChatService.getMessages(conversationUuid, page)
            // Wissel je van gesprek terwijl dit loopt, of sluit je het, dan hoort
            // deze pagina bij het vorige gesprek en mag hij nergens meer in
            // belanden - ook niet als er nu helemaal geen gesprek meer openstaat.
            if (state.currentChat?.uuid !== conversationUuid) return
            if (res.success && res.data) {
                const pageMsgs: Message[] = Array.isArray(res.data.data) ? res.data.data as unknown as Message[] : []
                // API returns newest-first; prepend to maintain ascending order
                // Ensure no duplicates via seenMessageIds
                const filtered = pageMsgs.filter((m: Message) => !state.seenMessageIds?.has(m.id))
                filtered.forEach((m: Message) => state.seenMessageIds?.add(m.id))
                // Older page -> prepend
                state.messages = [...filtered.reverse(), ...state.messages]
                // Update pagination
                const meta = (res.data as any).meta || {}
                messagePagination.currentPage = meta.current_page || page
                messagePagination.lastPage = meta.last_page || page
                messagePagination.hasMore = messagePagination.currentPage < messagePagination.lastPage
            }
        } catch (e) {
            console.error('Failed to fetch conversation messages', e)
        } finally {
            messagePagination.isLoadingPage = false
        }
    }

    // De detailpagina hangt zijn infinite scroll hieraan op, zodat hij stopt met
    // vragen zodra de oudste pagina binnen is.
    const hasMoreMessages = computed(() => messagePagination.hasMore)

    async function loadOlderMessages() {
        if (!state.currentChat || !messagePagination.hasMore) return
        const nextPage = (messagePagination.currentPage || 1) + 1
        await fetchConversationMessages(state.currentChat.uuid, nextPage)
    }

    // Shared success path of a send: insert the returned message and update the chat.
    function applySentResponse(conversationId: number, response: { success: boolean; message: string; data?: any }): SendResult {
        if (!(response.success && response.data)) {
            return { error: response.message || "" };
        }
        const newMessage: Message = {
            ...response.data.data,
            sender_id: currentUserId.value || response.data.data.sender_id,
        };

        // A slow upload can finish after the user opened another conversation.
        if (state.currentChat?.id === conversationId && !state.seenMessageIds?.has(newMessage.id)) {
            state.seenMessageIds?.add(newMessage.id);
            state.messages.push(newMessage);
        }

        const chat = state.chats.find(c => c.id === conversationId);
        if (chat) {
            chat.last_message = newMessage;
            chat.updated_at = newMessage.created_at;
        }

        return { message: newMessage };
    }

    // Send message
    async function sendMessage(conversationId: number, message: string, attachments?: any[], messageId?: number): Promise<SendResult | undefined> {
        if (!message.trim() && (!attachments || attachments.length === 0)) return;

        try {
            const response = await ChatService.sendMessage(conversationId, message, attachments, messageId);

            return applySentResponse(conversationId, response);
        } catch (error) {
            console.error('Failed to send message', error);
            return { error: "" };
        }
    }

    // Send a recorded voice note
    async function sendVoiceNote(conversationId: number, file: File): Promise<SendResult> {
        try {
            return applySentResponse(conversationId, await ChatService.sendVoiceNote(conversationId, file));
        } catch (error) {
            console.error('Failed to send voice note', error);
            return { error: "" };
        }
    }

    // Send typing indicator
    async function sendTypingIndicator(conversationId: number) {
        // Cancel previous in-flight typing request
        if (typingAbortController) {
            try { typingAbortController.abort() } catch { }
        }
        typingAbortController = new AbortController()

        // Debounce subsequent calls within 400ms window
        if (typingDebounceTimer) {
            clearTimeout(typingDebounceTimer)
        }
        typingDebounceTimer = window.setTimeout(async () => {
            try {
                await ChatService.typing(conversationId, { signal: typingAbortController?.signal })
            } catch (error) {
                // Ignore abort errors, log others
                if ((error as any)?.name !== 'AbortError') {
                    console.error('Failed to send typing indicator:', error)
                }
            }
        }, 400)
    }

    // Leave the chat
    async function leaveChat(conversationId: number) {
        try {
            return await ChatService.leave(conversationId);
        } catch (error) {
            console.error('Failed to leave the chat:', error)
        }
    }

    // Delete message
    async function deleteMessage(conversationId: number, message_id: number) {
        try {
            await ChatService.deleteMessage(conversationId, message_id);
        } catch (error) {
            state.error = error;
        }
    }

    // Handle real-time message received
    function handleMessageReceived(conversationId: number, message: Message) {
        // Add message to current messages if viewing this chat (avoid duplicates)
        if (!state.seenMessageIds?.has(message.id)) {
            state.seenMessageIds?.add(message.id);
            if (state.currentChat?.id === Number(conversationId)) {
                state.messages.push(message);
            }
        } else if (state.messages.findIndex(msg => msg.id === message.id) !== -1) {
            const existingIndex = state.messages.findIndex(msg => msg.id === message.id);
            state.messages[existingIndex] = { ...state.messages[existingIndex], ...message };
        }

        // Update chat's last message
        const chat = state.chats.find(c => c.id === Number(conversationId));
        if (chat) {
            chat.last_message = message;
            chat.updated_at = message.created_at;
            if (!state.currentChat || state.currentChat.id !== Number(conversationId)) {
                chat.unread_count += 1;
            }

            markChatAsRead(chat.id);
        } else {
            // If chat not in list, refresh chats
            fetchChats();
        }
    }

    // Handle message read event
    function handleMessageRead(conversationId: number) {
        const chat = state.chats.find(c => c.id === Number(conversationId));
        if (chat) {
            chat.unread_count = 0;
        }
    }

    // Handle user typing event
    function handleUserTyping(conversationId: number, userId: number) {
        // Ignore typing events from current user
        if (currentUserId.value && Number(userId) === Number(currentUserId.value)) {
            return;
        }
        if (state.currentChat?.id === Number(conversationId)) {
            state.typingUsers.add(userId);

            // Remove typing indicator after 3 seconds
            setTimeout(() => {
                state.typingUsers.delete(userId);
            }, 3000);
        }
    }

    // Clear current chat
    function clearCurrentChat() {
        state.currentChat = null;
        state.messages = [];
        state.typingUsers.clear();
        // Zonder dit filtert het volgende gesprek berichten weg die toevallig
        // dezelfde id hebben als iets dat je hier al gezien had.
        state.seenMessageIds?.clear();
    }

    // Get chat by ID
    function getChatById(id: number): Chat | undefined {
        return state.chats.find(chat => chat.id === Number(id));
    }

    // Get participant name (excluding current user)
    function getParticipantName(chat: Chat | null): string {
        if (!chat) return '';

        if (chat.type === 'group') return chat.title;

        const me = currentUserId.value;
        if (!me) return chat.title;

        const participant = chat.participants.find(p => p.id !== me);
        return participant ? participant.name : chat.title;
    }

    // Get participant avatar
    function getParticipantAvatar(chat: Chat | null): string | null {
        if (!chat) return null;

        return chat.avatar;
    }

    // Check if user is typing
    function isUserTyping(userId: number): boolean {
        return state.typingUsers.has(userId);
    }

    // Check if current user is sender
    function isCurrentUserSender(message: Message): boolean {
        return message.sender_id === currentUserId.value;
    }

    // Compare update and create times
    function isEdited(message: Message): boolean {
        const date1 = new Date(message.created_at);
        const date2 = new Date(message.updated_at);

        return (date2.getTime() - date1.getTime()) / 1000 >= 2;
    }

    // Helpers to consume raw Pusher payloads safely
    function onPusherMessageSent(payload: any) {
        const conversationId = payload?.conversationId || payload?.conversation_id || payload?.id;
        const raw = payload?.message || payload?.data || payload;
        if (!conversationId || !raw) return;
        const normalized: Message = {
            id: Number(raw.id),
            message: String(raw.message ?? raw.text ?? ''),
            sender_id: Number(raw.sender_id ?? raw.user_id ?? raw.senderId ?? 0),
            created_at: String(raw.created_at ?? raw.timestamp ?? new Date().toISOString()),
            updated_at: String(raw.updated_at ?? raw.timestamp ?? new Date().toISOString()),
            deleted_at: raw.deleted_at ? String(raw.deleted_at) : null,
            attachments: Array.isArray(raw.attachments) ? raw.attachments : (raw.attachments ? [raw.attachments] : []),
            // Without it a reviewed check-in arriving live shows as plain text until reload.
            metadata: raw.metadata ?? null,
        };
        handleMessageReceived(Number(conversationId), normalized);
    }

    function onPusherMessageDeleted(payload: any) {
        if (!payload?.message?.id) return;

        const existingIndex = state.messages.findIndex(msg => msg.id === Number(payload.message.id));
        state.messages[existingIndex] = { ...state.messages[existingIndex], ...payload.message };
    }

    function onPusherMessageRead(payload: any) {
        const conversationId = payload?.conversation_id || payload?.conversationId || payload?.id;
        if (!conversationId) return;
        handleMessageRead(Number(conversationId));
    }

    function onPusherUserTyping(payload: any) {
        const conversationId = payload?.conversationId || payload?.conversation_id || payload?.id;
        const userId = payload?.senderId || payload?.user_id || payload?.sender_id;
        if (!conversationId || !userId) return;
        handleUserTyping(Number(conversationId), Number(userId));
    }

    return {
        ...toRefs(state),
        // Computed
        unreadChatsCount,
        hasMoreMessages,
        // Methods
        fetchChats,
        setCurrentChat,
        markChatAsRead,
        sendMessage,
        sendVoiceNote,
        sendTypingIndicator,
        leaveChat,
        handleMessageReceived,
        handleMessageRead,
        handleUserTyping,
        clearCurrentChat,
        getChatById,
        getParticipantName,
        getParticipantAvatar,
        isUserTyping,
        isCurrentUserSender,
        formatMessageTime,
        isEdited,
        deleteMessage,
        // Upserts / Pusher helpers
        upsertChatFromChatListEvent,
        onPusherMessageSent,
        onPusherMessageDeleted,
        onPusherMessageRead,
        onPusherUserTyping,
        // Pagination
        fetchConversationMessages,
        loadOlderMessages,
    };
});
